import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { fmtCount } from '../format.js';
import { IconButton } from './IconButton.js';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/** `PAGE 3 / 12` with previous/next. */
export function Pagination({ page, pageCount, onPageChange, className }: PaginationProps) {
  const safeCount = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, page), safeCount);
  return (
    <nav aria-label="Pagination" className={cn('flex items-center gap-2', className)}>
      <IconButton icon={ChevronLeft} label="Previous page" size="sm" disabled={current <= 1} onClick={() => onPageChange(current - 1)} />
      <span className="font-mono text-2xs uppercase tracking-[0.18em] tabular-nums text-mute">
        Page <span className="text-ink">{fmtCount(current)}</span> / {fmtCount(safeCount)}
      </span>
      <IconButton icon={ChevronRight} label="Next page" size="sm" disabled={current >= safeCount} onClick={() => onPageChange(current + 1)} />
    </nav>
  );
}

export interface CountFooterProps {
  /** Total rows in the dataset. */
  total: number;
  /** Rows currently shown (after filters). Omit when equal to total. */
  shown?: number;
  /** Selected row count. */
  selected?: number;
  /** Row noun, e.g. `tests`, `records`. Default `rows`. */
  noun?: string;
  /** Extra right-aligned content (Pagination, StateBadge). */
  right?: ReactNode;
  className?: string;
}

/** Table footer: `4,218 tests · 120 shown · 1 selected` in mono, right slot for pagination. */
export function CountFooter({ total, shown, selected, noun = 'rows', right, className }: CountFooterProps) {
  const parts: string[] = [`${fmtCount(total)} ${noun}`];
  if (shown !== undefined && shown !== total) parts.push(`${fmtCount(shown)} shown`);
  if (selected !== undefined && selected > 0) parts.push(`${fmtCount(selected)} selected`);
  return (
    <div className={cn('flex items-center justify-between gap-4 border-t border-hair px-4 py-2', className)}>
      <p className="font-mono text-2xs uppercase tracking-[0.18em] tabular-nums text-mute" aria-live="polite">
        {parts.join(' · ')}
      </p>
      {right ? <div className="flex items-center gap-3">{right}</div> : null}
    </div>
  );
}
